import React, { useEffect, useRef, useState } from "react";
import Avatar from "@mui/material/Avatar";
import { Link, useNavigate } from "react-router-dom";
import { TbArrowBackUp } from "react-icons/tb";
import apiClient from "../api/apiClient";
import { handleErrors } from "../modules/handleErrors";
import { toast } from "react-toastify";
import { useIndexPageData } from "../contextapi/Indexpagedata";
import { useSelector } from "react-redux";
import LoadingSpinner from "../components/LoadingSpinner";
import { Image } from "primereact/image";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Tooltip } from "@mui/material";
import StripeCheckoutModal from "../modals/StripeCheckoutModal";
import CheckoutAuthModal from "../modals/CheckoutAuthModal";
import ShowMoreButtonStatic from "./ShowMoreButtonStatic";
import MockupCard from "../components/MockupCard";
import ReviewsSection from "../components/ReviewsSection";
import AddReviewModal from "../modals/AddReviewModal";
import { formatPrice, getActiveSale, getSalePriceDisplay } from "../utils/saleUtils";
import SalePriceLabel from "../components/SalePriceLabel";
import { useGuardedAsync } from "../hooks/useGuardedAsync";

const hasDisplayValue = (value) =>
  value != null && String(value).trim() !== "";

const DETAIL_ROW_STYLE = {
  display: "grid",
  gridTemplateColumns: "140px 1fr",
  columnGap: "16px",
  alignItems: "start",
  listStyle: "none",
};

const DetailRow = ({ label, children }) => (
  <li className="mb-2" style={DETAIL_ROW_STYLE}>
    <b className="fw-bold">{label}</b>
    <span style={{ wordBreak: "break-word" }}>{children}</span>
  </li>
);

const MockupBySection = ({ mockupData }) => (
  <div className="d-flex gap-3 align-items-center">
    <div>
      <p className="mb-0">
        <b>Mockup By:</b>{" "}
        <Link
          to={`/browse-mockups?name=${mockupData?.user_id}`}
          style={{ color: "#000000e8" }}
        >
          {mockupData?.user_name}
        </Link>
      </p>
    </div>
    <Avatar alt={mockupData?.user_name || "Seller"} src={mockupData?.user_profile} />
  </div>
);

const Mockup = () => {
  const currentUrl = window.location.origin;
  const { profiledata, fetchcart } = useIndexPageData();
  const { token, userType, isLoggedIn, userid, role } = useSelector(
    (state) => state.auth
  );
  const isAdmin = role === "A";

  const getCurrentURLParameters = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const params = {};
    for (const [key, value] of searchParams) {
      params[key] = value;
    }
    return params;
  };

  const { id } = getCurrentURLParameters();
  const navigate = useNavigate();
  const [mockupData, setMockupData] = useState({});
  const [loader, setLoader] = useState(false);
  const [buyLoader, setBuyLoader] = useState(false);
  const { loading: cartLoader, run: runAddToCart } = useGuardedAsync();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);
  const [animationClass, setAnimationClass] = useState("");
  const [showStripeModal, setShowStripeModal] = useState(0);
  const [showCheckoutAuth, setShowCheckoutAuth] = useState(false);
  const [checkoutAuthIntent, setCheckoutAuthIntent] = useState("buy");
  const pendingCheckoutAction = useRef(null);
  const [showAddReviewModal, setShowAddReviewModal] = useState(false);
  const [existingReview, setExistingReview] = useState(null);

  // Build ordered images array depending on `use_main_file_as_thumbnail` flag.
  // If true -> compressed file first, then thumbnails. If false -> thumbnails first, then compressed file.
  const images = (
    mockupData?.use_main_file_as_thumbnail
      ? [
          mockupData?.compressed_file,
          ...(mockupData?.thumbnails?.map((t) => t.image) || []),
        ]
      : [
          ...(mockupData?.thumbnails?.map((t) => t.image) || []),
          mockupData?.compressed_file,
        ]
  ).filter(Boolean);
  const [checkedItems, setCheckedItems] = useState([]);

  // Function to copy mockup ID to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Mockup ID copied to clipboard!");
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
        toast.success("Mockup ID copied to clipboard!");
      } catch (err) {
        toast.error("Failed to copy to clipboard");
      }
      document.body.removeChild(textArea);
    }
  };

  const fetchMockup = async () => {
    setLoader(true);
    let result;

    result = await apiClient.get(`/listings/${id}/`);
    setLoader(false);

    if (result.ok) setMockupData(result?.data);
  };

  const handleReviewAdded = () => {
    // Refresh mockup data to get updated reviews
    fetchMockup();
    setExistingReview(null);
  };

  const handleAddReviewClick = () => {
    setExistingReview(null);
    setShowAddReviewModal(true);
  };

  const handleEditReviewClick = (review) => {
    setExistingReview(review);
    setShowAddReviewModal(true);
  };

  useEffect(() => {
    if (id) {
      fetchMockup();
    }
  }, [id]);

  // Open WebSocket and emit 'mockup-view' when this page is rendered for a listing
  useEffect(() => {
    if (!id || ! mockupData?.user_id) return;
    
    const WS_URL =
      "wss://mockeenew-cwekd5c8h2fxecah.canadacentral-01.azurewebsites.net/ws/mockup-view/";
    let ws;

    try {
      ws = new WebSocket(WS_URL);
    } catch (err) {
      console.error("WebSocket init error:", err);
      return;
    }

    ws.onopen = () => {
      try {
        if(userid !== mockupData?.user_id) {
          ws.send(JSON.stringify({ type: "mockup-view", id }));
        }
      } catch (err) {
        console.error("WebSocket send error:", err);
      }
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
      } catch (err) {
        console.error("WebSocket message parse error:", err);
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    ws.onclose = (e) => {
      console.log("WebSocket closed", e);
    };

    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, [id , mockupData]);

  // Preload adjacent images for faster navigation
  useEffect(() => {
    if (images.length > 0) {
      const preloadImages = [];

      // Preload next image
      if (currentIndex < images.length - 1) {
        const nextImg = document.createElement("img");
        nextImg.src = images[currentIndex + 1];
        preloadImages.push(nextImg);
      }

      // Preload previous image
      if (currentIndex > 0) {
        const prevImg = document.createElement("img");
        prevImg.src = images[currentIndex - 1];
        preloadImages.push(prevImg);
      }
    }
  }, [currentIndex, mockupData]);

  const currentImageSrc = images[currentIndex] || "";

  mockupData?.is_bought;

  const addListingToCart = (listingId) => {
    runAddToCart(async () => {
      const result = await apiClient.post("/cartitems/", {
        product_id: listingId,
        modelType: "listing",
      });
      if (!result.ok) {
        handleErrors(result.data);
        return;
      }
      toast.success("Item successfully added to your cart!");
      fetchMockup();
      fetchcart();
    });
  };

  const openListingCheckout = () => {
    setCheckedItems([
      {
        product_id: id,
        modelType: "listing",
      },
    ]);
    setShowStripeModal(true);
  };

  const requireCheckoutAuth = (action) => {
    pendingCheckoutAction.current = action;
    setCheckoutAuthIntent(action === "cart" ? "cart" : "buy");
    setShowCheckoutAuth(true);
  };

  const handleAddToBag = (listingId) => {
    if (!isLoggedIn) return requireCheckoutAuth("cart");
    addListingToCart(listingId);
  };

  const handlePayment = () => {
    if (!isLoggedIn) return requireCheckoutAuth("buy");
    openListingCheckout();
  };

  const handleCheckoutAuthClose = () => {
    pendingCheckoutAction.current = null;
    setShowCheckoutAuth(false);
  };

  const handleCheckoutAuthSuccess = () => {
    pendingCheckoutAction.current = null;
    setShowCheckoutAuth(false);
    navigate("/cart");
  };

  const handleNextThumbnail = () => {
    if (currentIndex < (images.length || 0) - 1) {
      setImageLoading(true);
      setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
        setImageLoading(false);
      }, 500);
    }
  };

  const handlePreviousThumbnail = () => {
    if (currentIndex > 0) {
      setImageLoading(true);
      setTimeout(() => {
        setCurrentIndex(currentIndex - 1);
        setImageLoading(false);
      }, 500);
    }
  };

  const handlenNavigateSearchpage = (key, value, title, heading) => {
    navigate(`/Searchpage?${key}=${value}`, { state: { des: title, heading } });
  };

  const isPrevDisabled = currentIndex <= 0;
  const isNextDisabled = currentIndex >= (images.length || 0) - 1;
  const checkoutSale = getSalePriceDisplay(
    mockupData?.price,
    getActiveSale(mockupData)
  );
  const checkoutProduct = {
    title: mockupData?.title,
    price: formatPrice(
      checkoutSale.displayDiscounted ?? mockupData?.price
    ),
    image:
      (mockupData?.use_main_file_as_thumbnail
        ? mockupData?.compressed_file
        : mockupData?.thumbnails?.[0]?.image) || mockupData?.compressed_file,
    productId: mockupData?.id || id,
    modelType: "listing",
  };

  return (
    <>
      {loader ? (
        <div className="d-flex justify-content-center mt-5">
          <LoadingSpinner color="#FF3E1F" />
        </div>
      ) : (
        <>
        
          <section className="mockup py-5 my-md-5 my-3" style={{ marginBottom:"100px" }}>
          <div className="container mb-5">
            <TbArrowBackUp
                    style={{ fontSize: "32px", cursor: "pointer" }}
                    className="back-arrow-new shadow me-2 mb-3 rounded-5 p-1 arrow-button"
                    onClick={() => navigate(-1)}
                  />
                  </div>
            <div className="container">
              <div className="row">
                <div className="col-md-5">
                  <div className="bg-white shadow px-0 pb-2 py-0 rounded-4  overflow-hidden">
                    <div
                      className="d-flex justify-content-center slider-container position-relative"
                      style={{ minHeight: "400px", width: "100%" }}
                    >
                      {imageLoading ? (
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{
                            backgroundColor: "white",
                            width: "100%",
                            height: "auto",
                            minHeight: "400px",
                            objectFit: "contain",
                            maxWidth: "100%",
                          }}
                        >
                          <LoadingSpinner color="#FF3E1F" />
                        </div>
                      ) : (
                        <Image
                          style={{
                            objectFit: "contain",
                            width: "100%",
                            height: "auto",
                            maxWidth: "100%",
                          }}
                          src={currentImageSrc}
                          alt="Image"
                          preview
                        />
                      )}
                    </div>
                    <div className="d-flex justify-content-end gap-3 mt-4">
                      <div
                        className={`slider-button ${
                          isPrevDisabled ? "disabled" : ""
                        }`}
                      >
                        <ArrowBack
                          sx={{ fontSize: "30px" }}
                          onClick={handlePreviousThumbnail}
                          style={{
                            cursor: isPrevDisabled ? "not-allowed" : "pointer",
                          }}
                        />
                      </div>
                      <div
                        className={`slider-button ${
                          isNextDisabled ? "disabled" : ""
                        }`}
                      >
                        <ArrowForward
                          sx={{ fontSize: "30px" }}
                          onClick={handleNextThumbnail}
                          style={{
                            cursor: isNextDisabled ? "not-allowed" : "pointer",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 ms-md-5 mt-md-0 mt-4">
            
                  <h2 className="mb-0">{mockupData?.title}</h2>
                  <p className="mb-2 d-flex align-items-center gap-2">
                    ID: <span>{mockupData?.id}</span>
                    <Tooltip title="Copy to clipboard">
                      <ContentCopyIcon
                        onClick={() => copyToClipboard(mockupData?.id)}
                        style={{
                          fontSize: "16px",
                          cursor: "pointer",
                          color: "#C93312",
                          transition: "color 0.2s ease",
                        }}
                        onMouseEnter={(e) => (e.target.style.color = "#A02812")}
                        onMouseLeave={(e) => (e.target.style.color = "#C93312")}
                      />
                    </Tooltip>
                  </p>
                  <p>{mockupData?.short_description}</p>
                  {mockupData?.price && (() => {
                    const saleInfo = getActiveSale(mockupData);
                    const salePrice = getSalePriceDisplay(mockupData?.price, saleInfo);
                    if (salePrice.displayDiscounted == null) return null;
                    return (
                      <h2 className="fw-bold mt-3 d-flex align-items-baseline flex-wrap" style={{ fontSize: "42px" }}>
                        <SalePriceLabel
                          salePrice={salePrice}
                          strikeStyle={{
                            fontSize: "32px",
                            fontWeight: "normal",
                            color: "red",
                            textDecoration: "line-through",
                            marginRight: "8px",
                          }}
                        />
                      </h2>
                    );
                  })()}
                  <div className="d-flex gap-3 mt-4 flex-wrap">
                    {(mockupData?.is_bought ||
                      mockupData?.is_bundle_linked_and_bought ||
                      isAdmin) && (
                      <Link to={`/mockup-editor?id=${mockupData?.id}`}>
                        <button className="btn grey-btn rounded-3">Use</button>
                      </Link>
                    )}
                    {!mockupData?.is_bought &&
                      !profiledata?.is_suspended &&
                      !mockupData?.is_bundle_linked_and_bought &&
                      !isAdmin && (
                        <button
                          className="btn red-btn rounded-3"
                          disabled={buyLoader}
                          onClick={handlePayment}
                        >
                          {buyLoader ? "Buy..." : "Buy"}
                        </button>
                      )}
                    {!(mockupData?.is_cart ||
                      mockupData?.is_bought ||
                      mockupData?.is_bundle_linked_and_bought ||
                      profiledata?.is_suspended ||
                      isAdmin) && (
                      <button
                        className="btn red-btn rounded-3"
                        disabled={cartLoader}
                        onClick={() => handleAddToBag(mockupData?.id)}
                      >
                        {cartLoader ? "Adding..." : "Add to cart"}
                      </button>
                    )}
                  </div>
                  <hr />
                  <div className="details d-none d-lg-block">
                    <h2>Details</h2>
                    <div className="d-flex gap-3">
                      {mockupData.ai_models === "AI" && (
                        <img
                          style={{ width: "40px" }}
                          src="../assets/images/robot.png"
                          title={
                            mockupData.ai_models === "Mix"
                              ? "This mockup is created by using mix of techniques."
                              : "This mockup is AI generated."
                          }
                          alt=""
                        />
                      )}
                      {mockupData.ai_models === "Real" && (
                        <img
                          style={{ width: "40px" }}
                          src="../assets/images/camera.png"
                          title={
                            mockupData.ai_models === "Mix"
                              ? "This mockup is created by using mix of techniques."
                              : "This mockup represents a real product."
                          }
                          alt=""
                        />
                      )}
                      {mockupData.ai_models === "3D" && (
                        <img
                          style={{ width: "40px" }}
                          src="../assets/images/cube.png"
                          title={
                            mockupData.ai_models === "Mix"
                              ? "This mockup is created by using mix of techniques."
                              : "This mockup is generated in 3D software."
                          }
                          alt=""
                        />
                      )}
                      {mockupData.ai_models === "Mix" && (
                        <img
                          style={{ width: "40px" }}
                          src="../assets/images/Frame1.png"
                          title={
                            mockupData.ai_models === "Mix"
                              ? "This mockup is created by using mix of techniques."
                              : "This mockup is generated in 3D software."
                          }
                          alt=""
                        />
                      )}
                    </div>
                    <div className="row mt-4">
                      <div className="col-md-7">
                        <ul className="p-0 m-0">
                          <DetailRow label="Type:">
                            {mockupData?.ai_models}
                          </DetailRow>
                          {hasDisplayValue(mockupData?.product_name) && (
                            <DetailRow label="Product:">
                              {mockupData.product_name}
                            </DetailRow>
                          )}
                          {hasDisplayValue(mockupData?.print_provider) && (
                            <DetailRow label="Print Provider:">
                              {mockupData.print_provider}
                            </DetailRow>
                          )}
                          {hasDisplayValue(mockupData?.product_link) && (
                            <DetailRow label="Product link:">
                              <Link
                                to={mockupData.product_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-decoration-none"
                                style={{ color: "#000" }}
                              >
                                {mockupData.product_link}
                              </Link>
                            </DetailRow>
                          )}
                        </ul>
                      </div>
                      <div className="col-md-5">
                        <MockupBySection mockupData={mockupData} />
                      </div>
                    </div>
                    <div className="mt-3">
                      <h4 className="mb-2">Description</h4>
                      <p className="mb-0">{mockupData?.long_description}</p>
                      <div className="d-flex justify-content-end mt-4">
                        <a
                          href={`mailto:report@mockee.io?subject=Report a mockup [${id}]&body=Describe what is wrong with this mockup, and why it should be removed or changed:%0D%0A%0D%0A`}
                          style={{
                            color: "#C93312",
                            textDecoration: "underline",
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                        >
                          Report a mockup
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="details d-lg-none">
                    <h2>Details</h2>
                    <ul className="p-0 m-0 mt-3">
                      <DetailRow label="Type:">
                        {mockupData?.ai_models}
                      </DetailRow>
                      {hasDisplayValue(mockupData?.product_name) && (
                        <DetailRow label="Product:">
                          {mockupData.product_name}
                        </DetailRow>
                      )}
                      {hasDisplayValue(mockupData?.print_provider) && (
                        <DetailRow label="Print Provider:">
                          {mockupData.print_provider}
                        </DetailRow>
                      )}
                      {hasDisplayValue(mockupData?.product_link) && (
                        <DetailRow label="Product link:">
                          <Link
                            to={mockupData.product_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-decoration-none"
                            style={{ color: "#000" }}
                          >
                            {mockupData.product_link}
                          </Link>
                        </DetailRow>
                      )}
                    </ul>

                    <div className="mt-3">
                      <h4 className="mb-2">Description</h4>
                      <p className="mb-0">{mockupData?.long_description}</p>
                    </div>

                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-4">
                      <MockupBySection mockupData={mockupData} />
                      <a
                        href={`mailto:report@mockee.io?subject=Report a mockup [${id}]&body=Describe what is wrong with this mockup, and why it should be removed or changed:%0D%0A%0D%0A`}
                        style={{
                          color: "#C93312",
                          textDecoration: "underline",
                          fontSize: "14px",
                          cursor: "pointer",
                        }}
                      >
                        Report a mockup
                      </a>
                    </div>
                  </div>
                  {/* Reviews Section */}
                  <div className="mt-4">
                    <ReviewsSection
                      overallRating={mockupData?.overall_rating}
                      reviews={mockupData?.reviews || []}
                      mockupId={mockupData?.id}
                      showAddReviewButton={
                        (mockupData?.is_bought ||
                          mockupData?.is_bundle_linked_and_bought) &&
                        isLoggedIn
                      }
                      onAddReviewClick={handleAddReviewClick}
                      onEditReviewClick={handleEditReviewClick}
                      has_user_reviewed={mockupData?.has_user_reviewed}
                      mockupUserId ={mockupData?.user_id}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
          {mockupData?.same_category_listings?.length > 0 && (
            <section className="mt-4 mb-4" >
              <div className="container pt-3 pb-0"  style={{ backgroundColor: "#E3EBE0" , borderRadius:'20px' }}>
                <h2 className="text-start fw-bold mb-1">Similar Mockups</h2>
                <p className="text-start mb-1">Browse similar mockups.</p>

                <div className="row">
                  {mockupData?.same_category_listings?.map((item, index) => (
                    <MockupCard
                      key={index}
                      imageUrl={
                        item?.use_main_file_as_thumbnail
                          ? item?.compressed_file
                          : item?.thumbnails?.[0]?.image
                      }
                      title={item.title}
                      description={item.short_description}
                      price={item.price}
                      seller={item.user_name}
                      id={item.id}
                      item={item}
                      col={3}
                    />
                  ))}
                </div>
                <div className="flex mt-2">
                  <ShowMoreButtonStatic
                    onClick={() =>
                        handlenNavigateSearchpage(
                              "subcategory",
                              mockupData?.subcategory,
                              "Browse Mockups from same sub category.",
                              "Similar Mockups"
                      )
                    }
                  />
                </div>
              </div>
            </section>
          )}
          {mockupData?.same_creator_listings?.length > 0 && (
            <section className="mb-4 mt-4">
              <div className="container pt-3 pb-0" style={{ backgroundColor: "#FDF9EE" , borderRadius:'20px' }}>
                <h2 className="text-start fw-bold mb-1">
                  More from this seller
                </h2>
                <p className="text-start mb-1">Browse this seller mockups.</p>

                <div className="row">
                  {mockupData?.same_creator_listings?.map((item, index) => (
                    <MockupCard
                      key={index}
                      imageUrl={
                        item?.use_main_file_as_thumbnail
                          ? item?.compressed_file
                          : item?.thumbnails?.[0]?.image
                      }
                      title={item.title}
                      description={item.short_description}
                      price={item.price}
                      seller={item.user_name}
                      id={item.id}
                      item={item}
                      col={3}
                    />
                  ))}
                </div>

                <div className="flex mt-2">
                  <ShowMoreButtonStatic
                    onClick={() =>
                      navigate(`/browse-mockups?name=${mockupData?.user_id}`)
                    }
                  />
                </div>
              </div>
            </section>
          )}
        </>
      )}
      <StripeCheckoutModal
        open={showStripeModal}
        onClose={() => setShowStripeModal(false)}
        items={checkedItems}
        subtotal={checkoutSale.displayDiscounted ?? mockupData?.price}
      />
      <CheckoutAuthModal
        show={showCheckoutAuth}
        onHide={handleCheckoutAuthClose}
        onAuthenticated={handleCheckoutAuthSuccess}
        product={checkoutProduct}
        intent={checkoutAuthIntent}
      />
      <AddReviewModal
        show={showAddReviewModal}
        onClose={() => {
          setShowAddReviewModal(false);
          setExistingReview(null);
        }}
        mockupId={mockupData?.id}
        onReviewAdded={handleReviewAdded}
        existingReview={existingReview}
      />
    </>
  );
};

export default Mockup;
